import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Badge,
  MultiSelect,
} from '../../../components/ui';
import { API_BASE_URL } from '../../../config/apiConfig';

const MANUAL_CRON = 'manual';

const PRESETS = [
  { value: 'manual', label: 'None (manual only)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays (Mon–Fri)' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly (day of month)' },
  { value: 'advanced', label: 'Advanced (custom cron)' },
];

const DAYS_OF_WEEK = [
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
  { value: '0', label: 'Sun' },
];

const padHHMM = (h, m) =>
  `${String(Number(h ?? 0)).padStart(2, '0')}:${String(Number(m ?? 0)).padStart(2, '0')}`;

const splitTime = (timeStr) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(timeStr || '08:00'));
  if (!m) return { hh: 8, mm: 0 };
  return { hh: Number(m[1]), mm: Number(m[2]) };
};

/**
 * Try to detect a preset from a 5-field cron expression.
 */
const detectPreset = (cron) => {
  if (!cron || typeof cron !== 'string') return { preset: 'daily', time: '08:00', dow: '1', dom: '1' };
  if (cron.trim().toLowerCase() === MANUAL_CRON) {
    return { preset: 'manual', time: '08:00', dow: '1', dom: '1' };
  }
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { preset: 'advanced' };
  const [m, h, dom, , dow] = parts;
  const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  if (parts[2] === '*' && parts[3] === '*' && parts[4] === '*' && /^\d+$/.test(m) && /^\d+$/.test(h)) {
    return { preset: 'daily', time };
  }
  if (parts[2] === '*' && parts[3] === '*' && parts[4] === '1-5' && /^\d+$/.test(m) && /^\d+$/.test(h)) {
    return { preset: 'weekdays', time };
  }
  if (
    parts[2] === '*' &&
    parts[3] === '*' &&
    /^([0-6])(,[0-6])*$/.test(parts[4]) &&
    /^\d+$/.test(m) &&
    /^\d+$/.test(h)
  ) {
    return { preset: 'weekly', time, dow: parts[4] };
  }
  if (parts[3] === '*' && parts[4] === '*' && /^\d+$/.test(parts[2]) && /^\d+$/.test(m) && /^\d+$/.test(h)) {
    return { preset: 'monthly', time, dom };
  }
  return { preset: 'advanced' };
};

/**
 * Build a 5-field cron expression from the preset values.
 */
const buildCron = ({ preset, time, dow, dom }) => {
  const { hh, mm } = splitTime(time);
  switch (preset) {
    case 'manual':
      return MANUAL_CRON;
    case 'daily':
      return `${mm} ${hh} * * *`;
    case 'weekdays':
      return `${mm} ${hh} * * 1-5`;
    case 'weekly':
      return `${mm} ${hh} * * ${dow ?? '1'}`;
    case 'monthly':
      return `${mm} ${hh} ${dom ?? '1'} * *`;
    default:
      return '';
  }
};

/**
 * CronPresetField
 *
 * Renders preset radios/selects for daily / weekdays / weekly / monthly +
 * an "advanced" textbox. Mirrors the standard schedule builder UX.
 *
 * Props:
 *   value: cron expression string
 *   onChange(nextCron): callback when expression changes
 *   timezone: optional, used for the live preview
 */
const CronPresetField = ({ value, onChange, timezone = 'Europe/Athens' }) => {
  const initialDetected = detectPreset(value);
  const [preset, setPreset] = useState(initialDetected.preset);
  const [time, setTime] = useState(initialDetected.time || '08:00');
  const [dow, setDow] = useState(initialDetected.dow || '1');
  const [dom, setDom] = useState(initialDetected.dom || '1');
  const [advanced, setAdvanced] = useState(value || '');
  const [preview, setPreview] = useState({ valid: null, dates: [] });
  const emitChange = useCallback(
    (nextValue) => {
      if (typeof nextValue !== 'string') return;
      if (nextValue === value) return;
      onChange?.(nextValue);
    },
    [onChange, value],
  );
  // Seed a default expression once on mount for empty "new" forms.
  useEffect(() => {
    if (value && String(value).trim() !== '') return;
    const next = buildCron({ preset, time, dow, dom });
    if (next) emitChange(next);
  }, []);

  // Live preview - debounced fetch to /preview-cron.
  useEffect(() => {
    const expr = preset === 'advanced' ? advanced : buildCron({ preset, time, dow, dom });
    if (!expr) {
      setPreview({ valid: null, dates: [] });
      return;
    }
    if (expr.trim().toLowerCase() === MANUAL_CRON) {
      setPreview({ valid: true, dates: [], manualOnly: true });
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const token = localStorage.getItem('authToken');
        const url = new URL(`${API_BASE_URL}/report-schedules/preview-cron`);
        url.searchParams.set('expression', expr);
        url.searchParams.set('timezone', timezone || 'Europe/Athens');
        url.searchParams.set('count', '5');
        const res = await fetch(url.toString(), {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) {
          setPreview({ valid: false, dates: [] });
          return;
        }
        const data = await res.json();
        setPreview({
          valid: data?.valid !== false,
          dates: Array.isArray(data?.dates) ? data.dates : [],
        });
      } catch {
        setPreview({ valid: false, dates: [] });
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [preset, time, dow, dom, advanced, timezone]);

  const currentExpression =
    preset === 'advanced' ? advanced : buildCron({ preset, time, dow, dom });

  const isManualExpression =
    String(currentExpression || '').trim().toLowerCase() === MANUAL_CRON;

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-sm">Schedule Builder</CardTitle>
          <Badge variant="secondary" className="font-mono text-xs">
            {currentExpression || '—'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">

        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p.value}
              type="button"
              size="sm"
              variant={preset === p.value ? 'primary' : 'secondary'}
              onClick={() => {
                setPreset(p.value);
                if (p.value === 'advanced') {
                  setAdvanced(value || buildCron({ preset: 'daily', time, dow, dom }));
                  return;
                }
                if (p.value === 'manual') {
                  emitChange(MANUAL_CRON);
                  return;
                }
                const next = buildCron({ preset: p.value, time, dow, dom });
                if (next) emitChange(next);
              }}
            >
              {p.label}
            </Button>
          ))}
        </div>

      {preset === 'manual' && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No automatic runs. Use <strong>Run Now</strong> on the list (or the schedule actions) to send the report.
        </p>
      )}

      {preset !== 'advanced' && preset !== 'manual' && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4">
            <Label>Run Time</Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => {
                const { hh, mm } = splitTime(e.target.value);
                const nextTime = padHHMM(hh, mm);
                setTime(nextTime);
                const next = buildCron({ preset, time: nextTime, dow, dom });
                if (next) emitChange(next);
              }}
            />
          </div>
          {preset === 'weekly' && (
            <div className="md:col-span-8">
              <Label>Days of Week</Label>
              <div className="mt-2">
                <MultiSelect
                  options={DAYS_OF_WEEK}
                  value={String(dow || '')
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean)}
                  onChange={(selectedDays) => {
                    const normalized = (Array.isArray(selectedDays) ? selectedDays : [])
                      .map((v) => String(v).trim())
                      .filter(Boolean)
                      .sort((a, b) => Number(a) - Number(b));
                    const nextDow = (normalized.length > 0 ? normalized : ['1']).join(',');
                    setDow(nextDow);
                    const next = buildCron({ preset: 'weekly', time, dow: nextDow, dom });
                    if (next) emitChange(next);
                  }}
                  placeholder="Select one or more weekdays"
                  maxDisplay={4}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Pick one or more days.
              </p>
            </div>
          )}
          {preset === 'monthly' && (
            <div className="md:col-span-4">
              <Label>Day of Month</Label>
              <Input
                type="number"
                min={1}
                max={28}
                value={dom}
                onChange={(e) => {
                  const nextDom = e.target.value;
                  setDom(nextDom);
                  const next = buildCron({ preset, time, dow, dom: nextDom });
                  if (next) emitChange(next);
                }}
              />
            </div>
          )}
          {(preset === 'daily' || preset === 'weekdays') && (
            <div className="md:col-span-8 flex items-end">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {preset === 'daily'
                  ? 'Runs every day at the selected time.'
                  : 'Runs every Monday to Friday at the selected time.'}
              </p>
            </div>
          )}
        </div>
      )}

      {preset === 'advanced' && (
        <div className="space-y-2">
          <Label>Cron Expression</Label>
          <Input
            type="text"
            placeholder="0 8 * * 1"
            value={advanced}
            onChange={(e) => {
              setAdvanced(e.target.value);
              onChange?.(e.target.value);
            }}
          />
          <p className="text-xs text-gray-500 mt-1">
            Standard 5-field cron. Example: <code>0 8 * * 1</code> runs every
            Monday at 08:00.
          </p>
        </div>
      )}

        <Card className="border-dashed">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Live Preview</CardTitle>
              {preview.valid === false && (
                <Badge variant="destructive">Invalid expression</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0 text-xs">
            {preview.manualOnly || isManualExpression ? (
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Manual schedule — no upcoming automatic run times.
              </p>
            ) : preview.dates.length > 0 ? (
              <ul className="mt-1 list-disc list-inside text-gray-700">
                {preview.dates.slice(0, 5).map((d) => (
                  <li key={d}>{new Date(d).toLocaleString()}</li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 mt-1">
                {preview.valid === false
                  ? 'Fix the expression to see upcoming runs.'
                  : 'Enter a valid expression to preview upcoming runs.'}
              </p>
            )}
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
};

export default CronPresetField;
